import React, { useRef, useEffect, useState, useCallback } from '../../../../../_snowpack/pkg/react.js';
import { createPortal } from '../../../../../_snowpack/pkg/react-dom.js';
import { useLazyQuery } from '../../../../../_snowpack/pkg/@apollo/react-hooks.js';
import debounce from '../../../../../_snowpack/pkg/lodash/debounce.js';
import { useCombobox } from '../../../../../_snowpack/pkg/downshift.js';
import { useHistory } from '../../../../../_snowpack/pkg/react-router-dom.js';
import classNames from '../../../../../_snowpack/pkg/classnames.js';
import { Icon } from '../../../../css/index.js';
import { useKeyPress, useOnClickOutside, useTenant, useLanguage, useTenantLanguages } from '../../../../hooks/index.js';
import LoadingSpinner from '../../../loading-spinner.js';
import { SEARCH } from './graph.js';
import SearchItem from './search-item.js';
import { Button, Head, IconsWrapper, Wrapper, List, ListItem } from './styles.js';
var Type;

(function (Type) {
  Type["PRODUCT"] = "PRODUCT";
  Type["DOCUMENT"] = "DOCUMENT";
  Type["FOLDER"] = "FOLDER";
  Type["TOPIC"] = "TOPIC";
  Type["GRID"] = "GRID";
})(Type || (Type = {}));

const SEARCH_TYPES = [{
  type: Type.PRODUCT,
  icon: /*#__PURE__*/React.createElement(Icon.Product, null)
}, {
  type: Type.DOCUMENT,
  icon: /*#__PURE__*/React.createElement(Icon.Document, null)
}, {
  type: Type.FOLDER,
  icon: /*#__PURE__*/React.createElement(Icon.Folder, null)
}, // { type: 'shape', icon: <Icon.Shapes /> },
// { type: 'webhook', icon: <Icon.Hooks color="green" /> },
{
  type: Type.TOPIC,
  icon: /*#__PURE__*/React.createElement(Icon.Topics, {
    color: "green"
  })
}, {
  type: Type.GRID,
  icon: /*#__PURE__*/React.createElement(Icon.Grid, {
    color: "green"
  })
}];
const SEARCH_DEBOUNCE_TIME = 300;

function getTypesCount({
  type,
  data
}) {
  if (!data) {
    return 0;
  } else {
    var _data$search, _data$search$suggest;

    const typeAggregation = data === null || data === void 0 ? void 0 : (_data$search = data.search) === null || _data$search === void 0 ? void 0 : (_data$search$suggest = _data$search.suggest) === null || _data$search$suggest === void 0 ? void 0 : _data$search$suggest.aggregations.typesAggregation.find(ta => ta.type === type.toLowerCase());
    return (typeAggregation === null || typeAggregation === void 0 ? void 0 : typeAggregation.count) || 0;
  }
}
/**
 * TODO: currently we're not targeting the API when the search term is empty
 * for that reason we're resetting the search types and results only when there is a search term
 * since the search result still have the last search data.
 */


function SearchMenu(props) {
  const {
    isOpen,
    handleClose
  } = props;
  const searchRef = useRef(null);
  const WrapperRef = useRef(null);
  useKeyPress('Escape', handleClose);
  useOnClickOutside(WrapperRef, handleClose, {
    listen: isOpen
  });
  const history = useHistory();
  const {
    tenant: {
      id: tenantId
    }
  } = useTenant();
  const availableLanguages = useTenantLanguages();
  const [searchTerm, setSearchTerm] = useState('');
  const [searchTypes, setSearchTypes] = useState([]);
  const [searchQuery, {
    data,
    error,
    loading
  }] = useLazyQuery(SEARCH);
  const [items, setItems] = useState([]);
  const {
    language
  } = useLanguage();
  const {
    selectedItem,
    getMenuProps,
    getInputProps,
    getComboboxProps,
    getItemProps
  } = useCombobox({
    items,
    onSelectedItemChange: ({
      selectedItem
    }) => {
      if (selectedItem) {
        handleClose();
        history.push(selectedItem.path);
      }
    }
  });
  useEffect(() => {
    if (isOpen) {
      var _searchRef$current;

      (_searchRef$current = searchRef.current) === null || _searchRef$current === void 0 ? void 0 : _searchRef$current.select();
    }
  }, [isOpen]); // eslint-disable-next-line react-hooks/exhaustive-deps

  const debouncedSearch = useCallback(debounce(searchTerm => {
    searchQuery({
      variables: {
        tenantId,
        searchTerm,
        language
      }
    });
  }, SEARCH_DEBOUNCE_TIME), [tenantId, searchQuery, language]);
  useEffect(() => {
    if (searchTerm !== '') {
      debouncedSearch(searchTerm);
    }
  }, [searchTerm, debouncedSearch]);
  /**
   * Update the local cache of the result.
   * We do this because when making new
   * queries, the 'data' prop is undefined
   * when the request is in flight
   */

  useEffect(() => {
    var _data$search2, _data$search2$suggest;

    if (data !== null && data !== void 0 && (_data$search2 = data.search) !== null && _data$search2 !== void 0 && (_data$search2$suggest = _data$search2.suggest) !== null && _data$search2$suggest !== void 0 && _data$search2$suggest.edges) {
      const filteredEdges = data.search.suggest.edges.filter(({
        node
      }) => searchTypes.length ? searchTypes.includes(node.type) : true).filter(({
        node
      }) => !['shape', 'webhook'].includes(node.type)) || [];
      setItems(filteredEdges.map(({
        node
      }) => node));
    }
  }, [data, searchTypes]);

  const handleSearch = event => {
    setSearchTerm(event.target.value);
  };

  const onSearchTypeButtonClick = event => {
    event.stopPropagation();
    const {
      type
    } = event.currentTarget.dataset;

    if (!type) {
      return;
    }

    if (searchTypes.includes(type)) {
      setSearchTypes([...searchTypes].filter(t => t !== type));
    } else {
      setSearchTypes([...searchTypes, type]);
    }
  };

  return /*#__PURE__*/createPortal( /*#__PURE__*/React.createElement(Wrapper, {
    className: classNames({
      isOpen
    }),
    ref: WrapperRef
  }, /*#__PURE__*/React.createElement(Head, getComboboxProps(), /*#__PURE__*/React.createElement("input", getInputProps({
    ref: searchRef,
    placeholder: 'Search',
    onChange: handleSearch,
    value: searchTerm
  })), /*#__PURE__*/React.createElement(LoadingSpinner, {
    className: classNames('loading-indicator', {
      visible: loading
    }),
    size: 20
  }), /*#__PURE__*/React.createElement("button", {
    type: "button",
    onClick: handleClose
  }, /*#__PURE__*/React.createElement(Icon.Cancel, {
    color: "red",
    width: 15,
    height: 15
  }))), /*#__PURE__*/React.createElement(IconsWrapper, null, SEARCH_TYPES.map(({
    type,
    icon: SIcon
  }) => /*#__PURE__*/React.createElement(Button, {
    key: type,
    className: classNames({
      selected: searchTypes.includes(type)
    }),
    onClick: onSearchTypeButtonClick,
    "data-type": type
  }, SIcon, " ", getTypesCount({
    type,
    data: searchTerm.length ? data : {}
  })))), /*#__PURE__*/React.createElement(List, getMenuProps(), error ? /*#__PURE__*/React.createElement(ListItem, null, /*#__PURE__*/React.createElement("p", null, "Ouch. I wasn't able to search... Shame on me")) : !searchTerm.length ? /*#__PURE__*/React.createElement(ListItem, null, /*#__PURE__*/React.createElement("p", null, "Type something to find something")) : !loading && !items.length ? /*#__PURE__*/React.createElement(ListItem, null, /*#__PURE__*/React.createElement("p", null, "No results for \"", searchTerm, "\"", availableLanguages.length > 1 ? ` in language "${language}"` : '')) : items.map((node, index) => /*#__PURE__*/React.createElement(SearchItem, {
    key: node.id,
    node,
    index,
    getItemProps,
    selectedItem
  })))), document.body);
}

export default SearchMenu;